import {
  AddressLookup,
  Button,
  type LoqateAddressDetailsItem,
  LoqateAddressLookupService,
} from "@simplybusiness/mobius";
import { useSnowplowContext } from "@simplybusiness/services";
import type React from "react";
import type { FocusEvent } from "react";
import { useEffect, useMemo, useRef } from "react";
import { useAtomValue } from "jotai";
import {
  useQuestionnaire,
  useQuestionnaireActions,
  useGroups,
  useAnswer,
  questionnaireCombinedErrorsAtom,
} from "../../../../../../../features/questionnaire";
import { toPascalCase } from "../../../../../../../utils";
import type { QuestionComponentProps } from "../../types";
import type { QuestionProps } from "../../../types";
import { getStateCode } from "./utils/getStateCode";
import {
  ERRORS,
  US_STATE_ANSWER_KEY,
} from "../../../../../../../constants/constants";
import { useLocale } from "../../../../../../../hooks/useLocale";
import { getAddressLookupValue } from "./utils/getAddressLookupValue";

type AddressLookupGroupQuestionConfig = {
  apiKey: string;
  countryCode: string;
  manualEntryDisabled: boolean;
  selectedState?: string;
};

export const AddressLookupGroupQuestion = ({
  questionFieldProps,
  config,
  sectionTitle,
}: QuestionComponentProps) => {
  const { setVisible, setError, removeError } = useQuestionnaireActions();
  const groups = useGroups();
  const { questions, answers, validateQuestion, visibility } =
    useQuestionnaire();
  const typedConfig = config as AddressLookupGroupQuestionConfig;
  const { snowplow } = useSnowplowContext();
  const sendAddressLookupEvent = useRef<boolean>(false);

  const USStateAnswer = useAnswer(US_STATE_ANSWER_KEY) as string | undefined;
  const USStateCode = getStateCode(
    (typedConfig.selectedState ?? USStateAnswer) as string,
  );

  const {
    name,
    value,
    onChange,
    onFocus,
    questionLabelProps,
    errorMessage,
    helpInfo,
    ...otherProps
  } = questionFieldProps;

  const group = groups[name];
  const { isUS } = useLocale();

  // Check if any child question in the address group has an error
  const allErrors = useAtomValue(questionnaireCombinedErrorsAtom);
  const hasChildError =
    group?.names.some(childName => allErrors[childName]) ?? false;

  useEffect(() => {
    if (hasChildError) {
      setError(name, ERRORS.INVALID_ADDRESS);
    } else {
      removeError(name);
    }
  }, [hasChildError, name, setError, removeError]);

  const addressValue = useMemo<string>(
    () =>
      group?.names
        ? getAddressLookupValue({
            answers: group.names
              .map(name => answers[name])
              .filter((answer): answer is string => typeof answer === "string"),
            isUS,
          })
        : "",
    [group, answers, isUS],
  );

  const loqateService: LoqateAddressLookupService =
    new LoqateAddressLookupService({
      apiKey: typedConfig.apiKey,
      countries: [typedConfig.countryCode],
      filters: USStateCode ? { AdministrativeArea: USStateCode } : undefined,
    });

  const handleFocus = (event: FocusEvent) => {
    snowplow?.trigger("addressLookupFocused", {
      category: name,
      label: "Address lookup focused",
    });

    onFocus?.(event);
  };

  const handleOptionsDisplayed = () => {
    snowplow?.trigger("addressOptionsDisplayed", {
      category: name,
      label: "Address options displayed",
    });
  };

  const handleChange = (data: LoqateAddressDetailsItem) => {
    const addressData: Record<string, string> = {};

    // Get the group information to limit scope to only child questions of this address group
    const group = groups[name];

    if (group && group.kind === "address") {
      // Only iterate over the child questions that belong to this address group
      const childQuestions = group.names
        .map(name => questions.filter.getByName(name))
        .filter(
          (question): question is QuestionProps => question !== undefined,
        );

      childQuestions.forEach(question => {
        const mappingKey = toPascalCase(
          question.loqateMapping,
        ) as keyof LoqateAddressDetailsItem;
        const value = data[mappingKey] || "";

        // US-specific concatenation for BuildingNumber and Street
        // 'Street' is only set for a US journey; UK uses 'Line1'
        if (mappingKey === "Street") {
          addressData[question.name] = `${data.BuildingNumber} ${value}`;
        } else {
          // Remove any hyphenated part from the postal code (e.g., "90210-1234" becomes "90210")
          addressData[question.name] =
            mappingKey === "PostalCode" ? value.split("-")[0] : value;
        }
      });
    }

    const target = {
      name,
      value: addressData,
    };

    onChange?.({ target });

    // Validate group questions after address is selected
    // Create updated answers object that includes the new address data
    const nextAnswers = { ...answers, ...addressData };
    const groupQuestions = groups?.[name]?.names;
    for (const questionName of groupQuestions || []) {
      const question = questions.filter.getByName(questionName);
      if (question) {
        const response = validateQuestion({
          question,
          answers: nextAnswers,
          visibilityMap: visibility ?? {},
        });
        if (response.valid) {
          removeError(questionName);
        }
      }
    }
  };

  const showManualFields = () => {
    snowplow?.trigger("manualAddressClicked", {
      category: sectionTitle || questionFieldProps.label || name,
    });

    const group = groups[name];
    if (group && group.kind === "address") {
      setVisible(group.names);
    }
  };

  const handleAddressInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;

    if (value.length === 1 && !sendAddressLookupEvent?.current) {
      snowplow?.trigger("addressLookupTypedIn", {
        category: e.target.name,
        label: "First character input",
      });
      sendAddressLookupEvent.current = true;
    }
  };

  return (
    <div className="mobius-address-lookup__container">
      <AddressLookup
        addressLookupService={loqateService}
        {...otherProps}
        onChange={handleAddressInput}
        value={(value as string) || addressValue}
        placeholder="Enter your address"
        onAddressSelected={handleChange}
        onOptionsDisplayed={handleOptionsDisplayed}
        onFocus={handleFocus}
        minSearchLength={5}
        errorMessage={errorMessage}
      />
      {!typedConfig.manualEntryDisabled && (
        <Button size="sm" variant="link" onClick={showManualFields}>
          Enter address manually
        </Button>
      )}
    </div>
  );
};
